import { SimpleNoteCoreFields } from './adjustment-note-simple-fields';
import { SellNoteCoreFields, SellNoteLinesSection } from './adjustment-note-sell-fields';
import { usesSimpleLayout } from './adjustment-note-schema';
import { ValueChangeSection, type ValueChangeState } from './adjustment-value-change';
import { ParentInvoicePicker } from './adjustment-parent-picker';
import type { useAdjustmentForm } from './use-adjustment-form';
import type { InvoiceRow } from '../types';

export interface AdjustmentDialogFieldsProps {
  readonly form: ReturnType<typeof useAdjustmentForm>;
  readonly side: 'buy' | 'sell';
  readonly noteType: 'Credit' | 'Debit';
  readonly parents: InvoiceRow[];
  readonly parentsLoading: boolean;
  readonly existingInvoice: InvoiceRow | null;
  readonly isPending: boolean;
  readonly currencyLocked: boolean;
  readonly valueChange: ValueChangeState;
  readonly parentLocked: boolean;
}

export function AdjustmentDialogFields(props: AdjustmentDialogFieldsProps) {
  const { form } = props;
  const sellNote = props.side === 'sell';
  return (
    <>
      <ParentInvoicePicker
        parents={props.parents}
        loading={props.parentsLoading}
        editing={props.existingInvoice !== null}
        register={form.register}
        setValue={form.setValue}
        side={props.side}
        disabled={props.isPending}
        locked={props.parentLocked}
      />
      {/* Declared changes only exist on create — an edit revises testimony. */}
      {props.existingInvoice === null ? (
        <ValueChangeSection
          form={form}
          state={props.valueChange}
          noteType={props.noteType}
          disabled={props.isPending}
        />
      ) : null}
      <CoreFields {...props} sellNote={sellNote} />
      <ItemisedLinesSlot {...props} />
    </>
  );
}

function CoreFields(props: AdjustmentDialogFieldsProps & { readonly sellNote: boolean }) {
  if (props.sellNote) {
    return (
      <SellNoteCoreFields
        noteType={props.noteType}
        register={props.form.register}
        errors={props.form.formState.errors}
        disabled={props.isPending}
      />
    );
  }
  return (
    <SimpleNoteCoreFields
      register={props.form.register}
      errors={props.form.formState.errors}
      disabled={props.isPending}
      currencyLocked={props.currencyLocked}
      noteType={props.noteType}
      side={props.side}
    />
  );
}

function ItemisedLinesSlot(props: AdjustmentDialogFieldsProps) {
  if (usesSimpleLayout(props.noteType, props.side)) return null;
  // A declared change generates the single synthetic line itself — hiding the
  // editor keeps the operator from hand-editing figures the recompute owns.
  if (props.valueChange.active) return null;
  return (
    <SellNoteLinesSection
      noteType={props.noteType}
      control={props.form.control}
      register={props.form.register}
      setValue={props.form.setValue}
      disabled={props.isPending}
    />
  );
}
